import React, { useContext, useEffect, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import Help from './Help';
import Permissions from './Permissions';
import Events from './Events/Events';
import EventRecaps from './Events/EventRecaps';
import EventRecapGallery from './Events/EventRecapGallery';
import Event from './Events/EventRoute';
import EventRecap from './Events/EventRecap';
import Registration from './Events/Registration';
import Registrations from './Events/Registrations';
import Tickets from './Events/Tickets';
import CustomerCare from './CustomerCare';
import CampaignCodes from './CampaignCodes';
import CampaignCodeRequests from './CampaignCodeRequests';
import LeadActivity from './LeadActivity';
import Sessions from './Events/Sessions';
import Gallery from './Events/Gallery';
import Users from './Configuration/Users';
import Settings from './Configuration/Settings';
import GlobalReports from './Configuration/GlobalReports';
import Dealers from './Configuration/Dealers';
import DealerSyncJobs from './Configuration/DealerSyncJobs';
import Oops from '../main/Oops';
import Invite from './Configuration/Invite';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import SignIn from './SignIn';
import Profile from './Profile';
import MFA from './MFA';
import VerifyEmail from './VerifyEmail';
import FinishSignup from './FinishSignup';

import AuthShell from './layouts/AuthShell';
import AdminShell from './layouts/AdminShell';
import {
  RequireAuth,
  RequireVerifiedEmail,
  RequireMFA,
  RequireRole,
  RequireStaffView,
} from '../auth/RouteGuards';
import LoadingIndicator from '../common/LoadingIndicator';
import StaffCheckIn from './StaffCheckIn';
import { firebaseAuth } from '../../providers/AuthProvider';
import { STAFF_ROLE } from '../../Constants';

const StaffAware = ({ children }) => {
  const { user } = useContext(firebaseAuth);
  return Number(user?.role) === STAFF_ROLE ? <StaffCheckIn /> : children;
};

const DashboardLayout = () => {
  useEffect(() => {
    document
      .getElementById('loading-container')
      ?.classList.add('hide-loading-container');
  }, []);

  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-zinc-950">
          <LoadingIndicator size="lg" />
        </div>
      }
    >
      <DndProvider backend={HTML5Backend}>
        <Routes>
          {/* Auth-flow routes — centered card layout (AuthShell). */}
          <Route element={<AuthShell />}>
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/finish-signup" element={<FinishSignup />} />
            <Route path="/oops" element={<Oops />} />

            <Route element={<RequireAuth />}>
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route element={<RequireVerifiedEmail />}>
                <Route path="/mfa" element={<MFA />} />
              </Route>
            </Route>
          </Route>

          {/* Authenticated app — sidebar + topbar layout (AdminShell). */}
          <Route element={<RequireAuth />}>
            <Route element={<RequireVerifiedEmail />}>
              <Route element={<RequireMFA />}>
                <Route element={<RequireStaffView />}>
                  <Route element={<AdminShell />}>
                    <Route path="/" element={<StaffAware><Events /></StaffAware>} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/permissions" element={<Permissions />} />
                    <Route path="/event" element={<Event />} />
                    <Route path="/event/:id" element={<Event />} />
                    <Route path="/registrations" element={<StaffAware><Registrations /></StaffAware>} />
                    <Route path="/registrations/:id" element={<StaffAware><Registrations /></StaffAware>} />
                    <Route path="/tickets/:id" element={<Tickets />} />
                    <Route path="/event-recap" element={<EventRecap />} />
                    <Route path="/event-recap/:id" element={<EventRecap />} />
                    <Route path="/event-recaps" element={<EventRecaps />} />
                    <Route path="/event-recaps/gallery" element={<EventRecapGallery />} />
                    <Route path="/events-recaps/:id" element={<EventRecaps />} />
                    <Route path="/customer-care" element={<CustomerCare />} />
                    <Route path="/registration" element={<Registration />} />
                    <Route path="/registration/:id" element={<Registration />} />
                    <Route path="/registration/:id/:eventId" element={<Registration />} />
                    <Route
                      path="/campaign-code-requests"
                      element={<CampaignCodeRequests />}
                    />
                    <Route
                      path="/campaign-code-requests/:id"
                      element={<CampaignCodeRequests />}
                    />
                    <Route path="/campaign-codes" element={<CampaignCodes />} />
                    <Route path="/sessions" element={<Sessions />} />
                    <Route path="/sessions/:id" element={<Sessions />} />
                    <Route path="/gallery/:id" element={<Gallery />} />
                    <Route path="/lead-activity-report" element={<LeadActivity />} />

                    <Route element={<RequireRole minRole={1} />}>
                      <Route path="/users" element={<Users />} />
                      <Route path="/invite" element={<Invite />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/dealers" element={<Dealers />} />
                      <Route path="/dealers/jobs" element={<DealerSyncJobs />} />
                      <Route path="/dealers/jobs/:runId" element={<DealerSyncJobs />} />
                      <Route path="/global-reports" element={<GlobalReports />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
        </Routes>
      </DndProvider>
    </Suspense>
  );
};

export default DashboardLayout;
